import { useContext, useEffect, useRef } from "preact/hooks"
import { defaultSize, skeletonLoading } from "@/config"
import { useActions, useNostoAppState } from "@nosto/search-js/preact/hooks"
import { getCurrentUrlState, getQueryFromUrlState } from "@/mapping/url/getCurrentUrlState"
import { updateUrlFromQuery } from "@/mapping/url/updateUrl"
import { StoreContext, useConfig } from "@nosto/search-js/preact/common"
import { createSkeletonContent } from "./skeletonContent"
import { usePopState } from "@/hooks/usePopState"
import { searchQuerySignature } from "@/utils/searchQuerySignature"
import { useStyleFamilyCatalog } from "@/contexts/StyleFamilyCatalogContext"

export default function SearchQueryHandler() {
  const store = useContext(StoreContext)!
  const { newSearch } = useActions()
  const catalog = useStyleFamilyCatalog()

  const { initialized, query, filter, size, sort, loading, responseTotal, responseHits } = useNostoAppState(state => ({
    initialized: state.initialized,
    loading: state.loading,
    query: state.query?.query,
    filter: state.query?.products?.filter,
    size: state.query?.products?.size ?? defaultSize,
    sort: state.query?.products?.sort,
    responseTotal: state.response?.products?.total,
    responseHits: state.response?.products?.hits?.length
  }))

  const { pageType } = useConfig()
  const prevListingsSignature = useRef<string | null>(null)

  useEffect(() => {
    const query = getQueryFromUrlState()
    const isResultsPage = (pageType === "search" && query.query) || pageType === "category"
    if (isResultsPage) {
      if (skeletonLoading) {
        store.updateState(createSkeletonContent(query))
      }
      newSearch(query)
    }
  }, [store, newSearch, pageType])

  useEffect(() => {
    if (!initialized) {
      return
    }

    const fk = JSON.stringify(filter ?? [])
    const sk = JSON.stringify(sort ?? [])
    const qk = query ?? ""
    const listingsSig = `${fk}|${sk}|${qk}`

    if (prevListingsSignature.current === null) {
      prevListingsSignature.current = listingsSig
    } else if (prevListingsSignature.current !== listingsSig) {
      prevListingsSignature.current = listingsSig
      catalog?.resetUrlPage()
    }

    const url = getCurrentUrlState()
    updateUrlFromQuery({
      page: url.page,
      size,
      query,
      filter,
      sort
    })
  }, [initialized, loading, query, filter, sort, size, responseTotal, responseHits, catalog])

  usePopState(() => {
    const urlQuery = getQueryFromUrlState()
    const storeQuery = store.getState().query
    const urlSignature = searchQuerySignature(urlQuery)
    const storeSignature = searchQuerySignature(storeQuery)
    if (urlSignature === storeSignature) {
      return
    }
    newSearch(urlQuery)
  }, [store, newSearch])

  return null
}
